// @mui
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material"
// utils
import { fShortenNumber } from "minimals-template/components/utils/formatNumber"
// components
import Iconify from "minimals-template/components/Iconify"
import { omit } from "lib/omit"
import { forwardRef } from "react"
import { useMobile } from "lib/@components/mobile"

import { extractTextFromJSX } from "lib/extract-text-from-jsx"

// ----------------------------------------------------------------------

export function Summary({ title, total, icon, color, percent, time, label = "tasks", ...props }) {
    const isMobile = useMobile()
    const scale = isMobile ? "scale(0.9)" : "scale(1)"
    //
    return (
        <Stack
            {...props}
            data-cy="summary"
            className={`${props.className ?? ""} summary ${title?.replace(/\s+/g, "-").replace(/\W+/g, "-")}`}
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            sx={{ width: 1, pl: 0 }}
        >
            <Stack
                data-cy="summary-progress"
                alignItems="center"
                justifyContent="center"
                sx={{ position: "relative", transform: scale }}
            >
                <Iconify icon={icon} sx={{ color, width: 24, height: 24, position: "absolute" }} />

                <CircularProgress
                    variant="determinate"
                    value={percent}
                    size={56}
                    thickness={4}
                    sx={{ color, opacity: 0.48 }}
                />

                <CircularProgress
                    variant="determinate"
                    value={100}
                    size={56}
                    thickness={4}
                    sx={{ color: "grey.50016", position: "absolute", top: 0, left: 0, opacity: 0.48 }}
                />
            </Stack>

            <Stack spacing={isMobile ? 0.2 : 0.5} sx={{ pl: 1, minWidth: 80, pr: 1, transform: scale }}>
                <Tooltip title={title} arrow>
                    <Typography
                        component="div"
                        variant="h6"
                        sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                        }}
                        data-cy={`summary-criticality-${title}`}
                    >
                        {title}
                    </Typography>
                </Tooltip>

                <Typography variant="subtitle2" data-cy={`summary-criticality-${title}-count`}>
                    {fShortenNumber(total)}{" "}
                    <Box component="span" sx={{ color: "text.secondary", typography: "body2" }}>
                        {total === 1 ? label.singularize() : label.pluralize()}
                    </Box>
                </Typography>

                <Typography variant="subtitle2" sx={{ color }} data-cy="summary-time">
                    {time}
                </Typography>
            </Stack>
        </Stack>
    )
}

export const NumericSummary = forwardRef(function NumericSummary(
    { title, total, number, color, percent, time, label = "tasks", progressLabel = "total time", ...props },
    ref
) {
    const isMobile = useMobile()
    const scale = isMobile ? "scale(0.9)" : "scale(1)"
    return (
        <Stack
            {...omit(props, "onSelected")}
            data-cy="numeric-summary"
            className={`${props.className ?? ""} summary ${title?.replace?.(/\s+/g, "-").replace(/\W+/g, "-")}`}
            direction="row"
            alignItems="center"
            ref={ref}
            justifyContent="flex-start"
            sx={{ width: 1, pl: 0 }}
        >
            <Stack
                data-cy="numeric-summary-progress"
                alignItems="center"
                justifyContent="center"
                sx={{ position: "relative", transform: scale }}
            >
                <Box
                    data-cy="numeric-summary-annual-time"
                    sx={{
                        fontSize: "75%",
                        fontWeight: "bold",
                        color,
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        lineHeight: 1,
                        width: 40,
                        textAlign: "center",
                        transform: "translate3d(-50%, -50%, 0)",
                    }}
                >
                    {number}
                </Box>
                <CircularProgress
                    variant="determinate"
                    value={percent}
                    size={56}
                    thickness={4}
                    sx={{ color, opacity: 0.48 }}
                    aria-label={`${extractTextFromJSX(title)}: ${percent}% ${progressLabel}`}
                />

                <CircularProgress
                    variant="determinate"
                    value={100}
                    size={56}
                    thickness={4}
                    sx={{ color: "grey.50016", position: "absolute", top: 0, left: 0, opacity: 0.48 }}
                    aria-hidden="true"
                />
            </Stack>

            <Stack spacing={isMobile ? 0.2 : 0.5} sx={{ minWidth: 80, pl: 1, pr: 1, transform: scale }}>
                <Typography component="div" variant="h6" sx={{ whiteSpace: "nowrap" }}>
                    {title}
                </Typography>

                <Typography variant="subtitle2" data-cy="numeric-summary-count">
                    {fShortenNumber(total)}{" "}
                    <Box component="span" sx={{ color: "text.secondary", typography: "body2" }}>
                        {total === 1 ? label.singularize() : label.pluralize()}
                    </Box>
                </Typography>

                <Typography variant="subtitle2" sx={{ color }} data-cy="numeric-summary-time">
                    {time}
                </Typography>
            </Stack>
        </Stack>
    )
})
